import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useShop } from '../../context/ShopContext';

const { FiPlus, FiHeart, FiCheck } = FiIcons;

const ProductCard = ({ product }) => {
  const { addToCart } = useShop();
  const [isAdding, setIsAdding] = useState(false);
  const isOutOfStock = product.stock === 0;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (isOutOfStock) return;
    setIsAdding(true);
    addToCart(product);
    setTimeout(() => setIsAdding(false), 2000);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      className="group"
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-3xl mb-6 bg-luma-sand/10">
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
          {product.isNew && (
            <span className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-[9px] font-bold tracking-widest uppercase shadow-sm">
              New
            </span>
          )}
          {isOutOfStock && (
            <span className="bg-luma-text/90 text-white backdrop-blur px-3 py-1.5 rounded-full text-[9px] font-bold tracking-widest uppercase shadow-sm">
              Sold Out
            </span>
          )}
          {product.tag && !isOutOfStock && (
            <span className="bg-luma-rose/90 backdrop-blur px-3 py-1.5 rounded-full text-[9px] font-bold tracking-widest uppercase shadow-sm text-rose-900">
              {product.tag}
            </span>
          )}
        </div>

        <button 
          aria-label="Add to favorites"
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white text-luma-text focus:opacity-100"
        >
          <SafeIcon icon={FiHeart} className="text-lg" />
        </button>

        <img 
          src={product.image} 
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110 ${isOutOfStock ? 'grayscale opacity-50' : ''}`}
        />
        
        <div className="absolute inset-x-0 bottom-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[0.16,1,0.3,1]">
          <button 
            disabled={isOutOfStock}
            onClick={handleAdd}
            className={`w-full py-4 rounded-2xl flex items-center justify-center gap-3 text-xs font-bold tracking-widest uppercase shadow-2xl transition-all ${
              isAdding 
                ? 'bg-green-500 text-white' 
                : isOutOfStock 
                  ? 'bg-luma-sand text-luma-text/40 cursor-not-allowed' 
                  : 'bg-luma-text text-white hover:bg-luma-primary'
            }`}
          >
            <SafeIcon icon={isAdding ? FiCheck : FiPlus} className="text-lg" />
            {isAdding ? 'Added' : isOutOfStock ? 'Out of Stock' : 'Quick Add'}
          </button>
        </div>
      </div>
      
      <div className="space-y-2 px-1">
        <div className="flex justify-between items-start">
          <span className="text-[10px] text-luma-text/40 font-bold uppercase tracking-widest">{product.category}</span>
          <span className={`text-sm font-medium ${isOutOfStock ? 'text-luma-text/30 line-through' : 'text-luma-text'}`}>
            ${product.price.toFixed(2)}
          </span>
        </div>
        <h3 className="font-serif text-xl md:text-2xl text-luma-text group-hover:text-luma-primary transition-colors">{product.name}</h3>
        <p className="text-xs text-luma-text/50 font-light">{product.subtitle}</p>
      </div>
    </motion.div>
  );
};

export default ProductCard;